import * as React from 'react';
import { Text } from '../../display';
import { getLabelColor, buildContainerClasses, controlBaseClasses } from '../shared';

/**
 * Size variants for the Checkbox
 * - sm: 16px
 * - md: 18px (default, matches spec)
 * - lg: 22px
 */
export type CheckboxSize = 'sm' | 'md' | 'lg';

/**
 * Checkbox component props
 * Extends native input attributes, omitting 'type' and 'size' to avoid conflicts
 */
export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Label text displayed next to the checkbox
   */
  label?: string;
  /**
   * Whether the checkbox is in an indeterminate state
   * Used for partial selections in checkbox groups
   */
  indeterminate?: boolean;
  /**
   * Size variant of the checkbox
   * @default 'md'
   */
  size?: CheckboxSize;
  /**
   * Whether the checkbox is in an error state
   */
  error?: boolean;
  /**
   * Helper text displayed below the checkbox
   */
  helperText?: string;
  /**
   * Whether the checkbox is required. Renders a red asterisk next to the label
   * (matching the shared group / input label components) and forwards the
   * native `required` attribute to the input.
   */
  required?: boolean;
}

/**
 * Size classes for the checkbox control
 */
const sizeClasses: Record<CheckboxSize, { box: string; icon: string }> = {
  sm: { box: 'w-4 h-4', icon: 'w-2.5 h-2.5' }, // 16px
  md: { box: 'w-[18px] h-[18px]', icon: 'w-3 h-3' }, // 18px (spec)
  lg: { box: 'w-[22px] h-[22px]', icon: 'w-3.5 h-3.5' }, // 22px
};

/**
 * Base box styling classes (always applied)
 */
const baseBoxClasses = [...controlBaseClasses, 'rounded-[4px]'].join(' ');

/**
 * State configuration for checkbox styling
 */
interface CheckboxState {
  disabled: boolean;
  error: boolean;
  indeterminate: boolean;
}

/**
 * Get border and background classes for default (non-error, non-disabled) state
 */
function getDefaultStateClasses(indeterminate: boolean): string[] {
  const classes = [
    'border-secondary-400',
    'peer-checked:bg-primary-700',
    'peer-checked:border-primary-700',
  ];
  if (indeterminate) {
    classes.push('bg-primary-700', 'border-primary-700');
  }
  return classes;
}

/**
 * Get focus ring classes (applied when not disabled)
 */
function getFocusClasses(): string[] {
  return [
    'peer-focus-visible:ring-2',
    'peer-focus-visible:ring-primary-200',
    'peer-focus-visible:ring-offset-1',
  ];
}

/**
 * Get error state classes
 */
function getErrorStateClasses(indeterminate: boolean): string[] {
  const classes = [
    'border-error-700',
    'peer-checked:bg-error-700',
    'peer-checked:border-error-700',
  ];
  if (indeterminate) {
    classes.push('bg-error-700', 'border-error-700');
  }
  return classes;
}

/**
 * Get disabled state classes
 */
function getDisabledStateClasses(indeterminate: boolean): string[] {
  const classes = [
    'border-secondary-300',
    'bg-secondary-100',
    'peer-checked:bg-secondary-400',
    'peer-checked:border-secondary-400',
  ];
  if (indeterminate) {
    classes.push('bg-secondary-400', 'border-secondary-400');
  }
  return classes;
}

/**
 * Build the complete box classes string based on state
 */
function buildBoxClasses(boxSize: string, state: CheckboxState): string {
  const classes: string[] = [boxSize, baseBoxClasses];

  if (state.disabled) {
    classes.push(...getDisabledStateClasses(state.indeterminate));
    return classes.join(' ');
  }

  // Not disabled - add focus classes
  classes.push(...getFocusClasses());

  if (state.error) {
    classes.push(...getErrorStateClasses(state.indeterminate));
  } else {
    classes.push(...getDefaultStateClasses(state.indeterminate));
  }

  return classes.join(' ');
}

/**
 * Build icon classes string
 */
function buildIconClasses(iconSize: string, indeterminate: boolean): string {
  const classes = [
    iconSize,
    'text-white',
    'transition-opacity',
    'duration-150',
    'ease-out',
    'peer-checked:opacity-100',
    indeterminate ? 'opacity-100' : 'opacity-0',
  ];
  return classes.join(' ');
}


/**
 * Checkmark icon SVG component
 */
function CheckIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="2 6 5 9 10 3" />
    </svg>
  );
}

/**
 * Indeterminate (dash) icon SVG component
 */
function IndeterminateIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="2" y1="6" x2="10" y2="6" />
    </svg>
  );
}

/**
 * Checkbox component for boolean selections.
 *
 * Built following the design system specifications:
 * - Size: 18px x 18px
 * - Border: 2px neutral-400 (#A8A29E)
 * - Border radius: 4px
 * - Checked: Background primary-700 (#334E68), white checkmark (2px stroke)
 * - Focus: 2px ring primary-200 (#BCCCDC)
 * - Label spacing: 8px from control
 *
 * @example
 * // Basic usage
 * <Checkbox label="Accept terms and conditions" />
 *
 * @example
 * // Indeterminate state
 * <Checkbox label="Select all" indeterminate />
 *
 * @example
 * // Error state
 * <Checkbox label="Required field" error helperText="This field is required" />
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      indeterminate = false,
      size = 'md',
      error = false,
      helperText,
      required = false,
      className = '',
      disabled = false,
      id: providedId,
      checked,
      defaultChecked,
      onChange,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    const reactId = React.useId();
    const checkboxId = providedId || `checkbox-${reactId}`;
    const helperId = `checkbox-helper-${reactId}`;

    React.useEffect(() => {
      /* v8 ignore start */
      if (internalRef.current) {
        /* v8 ignore stop */
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    // Compute derived values using helper functions
    const messageId = helperText ? helperId : undefined;
    const computedAriaDescribedBy = [ariaDescribedBy, messageId]
      .filter(Boolean)
      .join(' ') || undefined;

    const { box: boxSize, icon: iconSize } = sizeClasses[size];
    const state: CheckboxState = { disabled, error, indeterminate };

    const boxClasses = buildBoxClasses(boxSize, state);
    const iconClasses = buildIconClasses(iconSize, indeterminate);
    const containerClasses = buildContainerClasses(disabled, className);
    const labelColor = getLabelColor(disabled, error);
    const labelCursor = disabled ? 'cursor-not-allowed' : 'cursor-pointer';
    const helperColor = error ? 'error' : 'muted';

    return (
      <div className="w-fit">
        <label className={containerClasses}>
          <input
            ref={internalRef}
            type="checkbox"
            id={checkboxId}
            disabled={disabled}
            required={required}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            aria-invalid={error}
            aria-describedby={computedAriaDescribedBy}
            className="peer sr-only"
            {...props}
          />

          <span className={boxClasses} aria-hidden="true">
            {indeterminate ? (
              <IndeterminateIcon className={iconClasses} />
            ) : (
              <CheckIcon className={iconClasses} />
            )}
          </span>

          {label && (
            <Text as="span" size="base" color={labelColor} className={labelCursor}>
              {label}
              {required && (
                <span className="ml-0.5 text-error-700" aria-hidden="true">
                  *
                </span>
              )}
            </Text>
          )}
        </label>

        {helperText && (
          <Text
            id={messageId}
            size="xs"
            color={helperColor}
            className="mt-1 ml-[26px]"
          >
            {helperText}
          </Text>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
